import * as jwtDecode from 'jwt-decode';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

import { ConfigService } from 'src/app/_services/config.service';
import { SessionStorageService } from 'src/app/_services/session-storage.service';

@Injectable()
export class AuthenticationService {
  jsonWebToken$: Observable<string>;
  jsonWebTokenPayload$: Observable<any>;
  jsonWebTokenFull$: Observable<any>;

  constructor(
    private configService: ConfigService,
    private http: HttpClient,
    private router: Router,
    private sessionStorageService: SessionStorageService
  ) {
    this.jsonWebToken$ = sessionStorageService.select('jsonToken', null);
    this.jsonWebTokenPayload$ = this.jsonWebToken$.pipe(
      map(jwt => {
        if (!jwt) {
          return null;
        }
        return jwtDecode(jwt);
      })
    );
    this.jsonWebTokenFull$ = this.jsonWebToken$.pipe(
      map(jwt => {
        if (!jwt) {
          return null;
        }
        return { payload: jwtDecode(jwt), jwt };
      })
    );
  }

  get isLoggedIn$(): Observable<boolean> {
    return this.jsonWebToken$.pipe(
      map(jwt => jwt !== null && jwt !== undefined)
    );
  }

  get allowedRoutes$(): Observable<string[]> {
    return this.jsonWebTokenPayload$.pipe(
      map(tokenPayload => {
        let role = tokenPayload.role ? tokenPayload.role.toUpperCase() : '';
        if (!role && tokenPayload.roles && tokenPayload.roles.length > 0) {
          role = tokenPayload.roles[0];
        }

        if (!role || role === '') {
          return [];
        }

        switch (role) {
          case 'CLIENT_ADMIN':
            return ['admin-manage-users'];
          case 'SERVICE_ADMIN':
            return ['admin-manage-users', 'admin-audit-log', 'manage-news'];
          case 'BROKERAGE':
            return ['manage-users'];
          case 'ADVISOR':
            return ['manage-users'];
          case 'CONSULTANT':
            return ['manage-users'];
          case 'COMPLIANCE_OFFICER':
            return ['instruction'];
          case 'AUTH_SIGNATORY':
            return ['fee-upload'];
          default:
            return [];
        }
      })
    );
  }

  get getUserRoles(): Observable<string[]> {
    return this.jsonWebTokenPayload$.pipe(
      map(tokenPayload => tokenPayload.roles),
      map(roles => {
        return roles;
      })
    );
  }

  getCurrentUser(username): any {
    const REST_URL = `${this.configService.apiUrl()}/users/${username}`;
    return this.http.get(REST_URL).pipe(tap((response: any) => response.user));
  }

  login(username: string, password: string): Observable<any> {
    const REST_URL = `${this.baseUrl()}/login`;
    return this.http
      .post(REST_URL, {
        username,
        password
      })
      .pipe(
        tap(({ jsonWebToken }) => {
          this.sessionStorageService.set('jsonToken', jsonWebToken);
        })
      );
  }

  logout(): void {
    this.clearToken();
    // redirect to portal
    window.top.location.href = this.configService.get('mwiPortalUrl');
  }

  clearToken(): void {
    this.sessionStorageService.remove('jsonToken');
  }

  requestPasswordReset(username: string): Observable<any> {
    const REST_URL = `${this.baseUrl()}/requestPasswordReset`;
    return this.http.post(REST_URL, { username });
  }

  resetPassword(password: string): Observable<any> {
    const REST_URL = `${this.baseUrl()}/resetPassword`;
    return this.http.post(REST_URL, { password }).pipe(
      tap(() => {
        this.sessionStorageService.remove('jsonToken');
      })
    );
  }

  updatePassword(
    currentPassword: string,
    newPassword: string
  ): Observable<any> {
    const REST_URL = `${this.baseUrl()}/updatePassword`;
    return this.http.post(REST_URL, {
      currentPassword,
      newPassword
    });
  }

  changeRole(role: string): Observable<any> {
    const REST_URL = `${this.baseUrl()}/changeRole`;
    return this.http
      .post(REST_URL, {
        role
      })
      .pipe(
        tap(({ jsonWebToken }) => {
          this.sessionStorageService.set('jsonToken', jsonWebToken);
        })
      );
  }

  private baseUrl(): string {
    return `${this.configService.apiUrl()}/auth`;
  }
}
